/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { TEAMS } from "../../data/constants";
import { financeStats, fmtMoney, fmtMoneyShort } from "../../lib/appUtils";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";

/**
 * CollectionsChartsPanel —— 收款报表 3 图。
 *   ① 各小组收款对比（已收 / 应收）  ② 收款比例饼图  ③ 按月回款趋势
 *
 * 展示需求（2026-10-21）：取消财务报表弹窗后，这 3 个图随分公司汇总与盈利对比
 * 直接显示在「项目财务」顶部；组件只负责图表，不重复增加报表入口或独立滚动容器。
 *
 * 数据口径与改前**完全一致**：由调用方传入 `projects`（当前用户可见、且已按分公司 chip 筛过的列表），
 * 组件内部按小组 / 按月份聚合，不改任何算法。原来 testid（cw-team-chart / cw-ratio-pie /
 * cw-monthly-trend / cw-trend-year / cw-trend-total）保持不变，便于既有 QA 与文档继续适用。
 */
export default function CollectionsChartsPanel({ projects }) {
  const { lang } = useApp();
  const zh = lang === "zh";
  const nowY = new Date().getFullYear();
  const [trendYear, setTrendYear] = useState(nowY);

  const scoped = projects || [];
  const stats = financeStats(scoped);

  // ① 各小组收款对比
  const byTeam = TEAMS.map((tm, i) => {
    const s = financeStats(scoped.filter((p) => p.team === tm.id));
    return { name: zh ? tm.zh : tm.name, collected: s.collected, receivable: s.receivable, color: ["#4F6BFF", "#7B5CFF", "#06b6d4", "#10b981", "#f59e0b"][i % 5] };
  });

  // ② 收款比例
  const uncollected = Math.max(0, stats.totalContract - stats.collected);
  const ratioData = [
    { name: zh ? "已收款" : "Collected", value: stats.collected, color: "#16a34a" },
    { name: zh ? "未收款" : "Uncollected", value: uncollected, color: "#e11b22" },
  ].filter((d) => d.value > 0);

  // ③ 按月回款趋势（按收款登记日期 paidAt 汇总，可切换年份）
  const monthly = useMemo(() => {
    const arr = Array.from({ length: 12 }, (_, i) => ({ m: zh ? `${i + 1}月` : `${i + 1}`, amt: 0 }));
    for (const p of scoped) for (const pay of p.payments || []) {
      if (pay.status !== "paid") continue;
      const d = pay.paidAt || "";
      if (!d.startsWith(String(trendYear))) continue;
      const mi = parseInt(d.slice(5, 7), 10) - 1;
      if (mi >= 0 && mi < 12) arr[mi].amt += Number(pay.paidAmount ?? pay.amount ?? 0);
    }
    return arr;
  }, [scoped, trendYear, zh]);
  const trendTotal = monthly.reduce((s, x) => s + x.amt, 0);

  return (
    <div className="space-y-5" data-testid="collections-charts-panel">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2" data-testid="cw-team-chart">
          <h3 className="font-semibold text-slate-900 mb-4">{zh ? "各小组收款对比 (已收 / 应收)" : "Collection by Team (Collected / Receivable)"}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={byTeam} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" name={zh ? "已收款" : "Collected"} fill="#16a34a" radius={[5, 5, 0, 0]} />
              <Bar dataKey="receivable" name={zh ? "应收款" : "Receivable"} fill="#4F6BFF" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cw-ratio-pie">
          <h3 className="font-semibold text-slate-900 mb-1">{zh ? "收款比例" : "Collection Ratio"}</h3>
          <p className="text-xs text-slate-400 mb-2">{zh ? "合同总额" : "Total Contract"}: <span className="font-semibold text-slate-700">{fmtMoneyShort(stats.totalContract)}</span></p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={ratioData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={48} paddingAngle={2} isAnimationActive={false}>
                {ratioData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="cw-monthly-trend">
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">{zh ? "按月回款趋势" : "Monthly Collections"}</h3>
            <p className="text-xs text-slate-400">{trendYear}{zh ? " 年实收合计 " : " total collected "}<b className="text-emerald-600" data-testid="cw-trend-total">{fmtMoney(trendTotal)}</b>{zh ? " · 按收款登记日期(paidAt)汇总" : ""}</p>
          </div>
          <select data-testid="cw-trend-year" className="text-sm rounded-md border border-slate-200 px-2 py-1.5" value={trendYear} onChange={(e) => setTrendYear(Number(e.target.value))}>
            {[0, 1, 2, 3].map((i) => <option key={i} value={nowY - i}>{nowY - i}</option>)}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={monthly} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip formatter={(v) => fmtMoney(v)} />
            <Bar dataKey="amt" name={zh ? "实收" : "Collected"} fill="#16a34a" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
